"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

// `necessary` is always granted (session, security, CSRF, region preference).
export type ConsentCategory =
  | "necessary"
  | "preferences"
  | "statistics"
  | "marketing";

export type Consent = Record<ConsentCategory, boolean>;

export type ConsentCategoryInfo = {
  key: ConsentCategory;
  label: string;
  description: string;
  required?: boolean;
};

export const CONSENT_CATEGORIES: ConsentCategoryInfo[] = [
  {
    key: "necessary",
    label: "Necessary",
    description:
      "Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.",
    required: true,
  },
  {
    key: "preferences",
    label: "Preferences",
    description:
      "Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.",
  },
  {
    key: "statistics",
    label: "Statistics",
    description:
      "Statistic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.",
  },
];

const STORAGE_KEY = "tf:consent";
const CONSENT_VERSION = 1;

const DENIED: Consent = {
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
};

const GRANTED: Consent = {
  necessary: true,
  preferences: true,
  statistics: true,
  marketing: true,
};

type StoredConsent = {
  v: number;
  ts: number;
  consent: Consent;
};

// Cached snapshot so useSyncExternalStore gets a stable reference when
// the localStorage value is unchanged.
let snapshotCache: { raw: string | null; value: StoredConsent | null } = {
  raw: null,
  value: null,
};

function getStoredSnapshot(): StoredConsent | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return snapshotCache.value;
  }
  if (raw === snapshotCache.raw) return snapshotCache.value;
  let parsed: StoredConsent | null = null;
  try {
    if (raw) {
      const obj = JSON.parse(raw) as StoredConsent;
      if (obj && obj.v === CONSENT_VERSION) parsed = obj;
    }
  } catch {
    parsed = null;
  }
  snapshotCache = { raw, value: parsed };
  return parsed;
}

function getStoredServerSnapshot(): StoredConsent | null {
  return null;
}

function subscribeToStored(callback: () => void) {
  window.addEventListener("storage", callback);
  window.addEventListener("tf:consent-change", callback);
  return () => {
    window.removeEventListener("storage", callback);
    window.removeEventListener("tf:consent-change", callback);
  };
}

function writeStored(consent: Consent) {
  if (typeof window === "undefined") return;
  try {
    const payload: StoredConsent = {
      v: CONSENT_VERSION,
      ts: Date.now(),
      consent,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(
      new CustomEvent("tf:consent-change", { detail: consent }),
    );
  } catch {
    // ignore quota / privacy errors
  }
}

type ConsentContextValue = {
  consent: Consent;
  hasDecided: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePartial: (next: Partial<Consent>) => void;
  openSettings: () => void;
  closeSettings: () => void;
  isSettingsOpen: boolean;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  // localStorage is the source of truth; writeStored dispatches
  // `tf:consent-change` to wake subscribers, and `storage` syncs other tabs.
  const stored = useSyncExternalStore(
    subscribeToStored,
    getStoredSnapshot,
    getStoredServerSnapshot,
  );
  const consent: Consent = stored?.consent ?? DENIED;
  const hasDecided = stored !== null;

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Opens the modal on the #manage-cookies hash, so editors can place a
  // "Manage Cookies" link in rich-text content without a custom blok.
  useEffect(() => {
    const HASH = "#manage-cookies";
    const check = () => {
      if (window.location.hash === HASH) {
        setIsSettingsOpen(true);
        // Clear the hash so the same link can re-open the modal later.
        history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, []);

  const persist = useCallback((next: Consent) => {
    writeStored(next);
  }, []);

  const acceptAll = useCallback(() => {
    persist(GRANTED);
    setIsSettingsOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist(DENIED);
    setIsSettingsOpen(false);
  }, [persist]);

  const savePartial = useCallback(
    (next: Partial<Consent>) => {
      persist({ ...consent, ...next, necessary: true });
      setIsSettingsOpen(false);
    },
    [consent, persist],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      hasDecided,
      acceptAll,
      rejectAll,
      savePartial,
      openSettings: () => setIsSettingsOpen(true),
      closeSettings: () => setIsSettingsOpen(false),
      isSettingsOpen,
    }),
    [consent, hasDecided, acceptAll, rejectAll, savePartial, isSettingsOpen],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside ConsentProvider");
  return ctx;
}
